import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { usePageMeta } from "@/hooks/usePageMeta";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "lucide-react";

interface Blog {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  category: string | null;
  published_at: string | null;
  created_at: string;
}

export default function BlogListPage() {
  usePageMeta(
    "Blog — Shattara AI",
    "Study tips, AI tutorials, and productivity guides to help students study smarter with AI-powered tools.",
  );

  const [posts, setPosts] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("blogs")
      .select("id, title, slug, excerpt, category, published_at, created_at")
      .eq("published", true)
      .order("published_at", { ascending: false })
      .then(({ data }) => {
        setPosts(data ?? []);
        setLoading(false);
      });
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Shattara AI Blog",
    description:
      "Study tips, AI tutorials, and productivity guides for students.",
    url: "https://shattaraai.com/blog",
    publisher: {
      "@type": "Organization",
      name: "Shattara AI",
      logo: { "@type": "ImageObject", url: "https://shattaraai.com/og-image.png" },
    },
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navigation />
      <main id="main-content" className="flex-1">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />

        {/* Hero */}
        <section className="py-16 md:py-24 border-b border-border">
          <div className="container mx-auto px-6 text-center max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
              Blog
            </h1>
            <p className="text-lg text-muted-foreground">
              Study tips, AI tutorials, and productivity guides to help you study
              smarter — not longer.
            </p>
          </div>
        </section>

        {/* Grid */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-6">
            {loading ? (
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-border bg-card animate-pulse h-56"
                  />
                ))}
              </div>
            ) : (
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {posts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group rounded-xl border border-border bg-card p-6 hover:border-primary/40 hover:shadow-md transition-all flex flex-col"
                  >
                    {post.category && (
                      <Badge
                        variant="secondary"
                        className="w-fit mb-3 text-xs"
                      >
                        {post.category}
                      </Badge>
                    )}
                    <h2 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-2 line-clamp-2">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-4 flex-1">
                        {post.excerpt}
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-auto pt-3 border-t border-border">
                      <Calendar className="h-3.5 w-3.5" />
                      <time>
                        {new Date(
                          post.published_at ?? post.created_at,
                        ).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </time>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
